import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ClipboardList } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { DOC_TYPES, labelFor, formatDate } from "@/lib/constants";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function PortalRequests() {
    const { activeClientId, clients } = useAuth();
    const [requests, setRequests] = useState([]);
    useEffect(() => {
        const params = activeClientId ? { client_id: activeClientId } : {};
        api.get("/requests/", { params }).then((r) => setRequests(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, [activeClientId]);

    const open = requests.filter((r) => r.status === "open" || r.status === "in_progress");
    const done = requests.filter((r) => r.status === "completed");
    const showEntityHint = clients && clients.length > 1;

    return (
        <div>
            <div className="co-label">Requests</div>
            <h1 className="font-display text-3xl tracking-tight font-medium mt-1">What your accountant has asked for</h1>
            <p className="text-sm text-[#5E636E] mt-2">Each request lists what&apos;s needed. Tap one to upload directly.</p>
            {showEntityHint && (
                <p className="mt-2 text-[11px] text-[#5E636E]" data-testid="portal-requests-entity-hint">
                    Showing requests for the entity selected in the top-right switcher.
                </p>
            )}

            <Tabs defaultValue="open" className="mt-6">
                <TabsList data-testid="portal-requests-tabs">
                    <TabsTrigger value="open" data-testid="pr-tab-open">Outstanding ({open.length})</TabsTrigger>
                    <TabsTrigger value="done" data-testid="pr-tab-done">Completed ({done.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="open" className="mt-4">
                    <RequestList list={open} emptyMsg="You're all caught up — nothing outstanding." />
                </TabsContent>
                <TabsContent value="done" className="mt-4">
                    <RequestList list={done} emptyMsg="No completed requests yet." />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function RequestList({ list, emptyMsg }) {
    if (list.length === 0) {
        return <EmptyState icon={ClipboardList} title={emptyMsg} testId="portal-requests-empty" />;
    }
    return (
        <ul className="space-y-3" data-testid="portal-requests-list">
            {list.map((r) => (
                <li key={r.id} className="co-card co-card-pad" data-testid={`portal-req-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="font-display text-lg font-medium">{r.title}</div>
                            <div className="text-xs text-[#5E636E] mt-1">Due {formatDate(r.due_date)} · {r.submission_ids?.length || 0} submitted</div>
                        </div>
                        <StatusBadge status={r.status} />
                    </div>
                    <p className="mt-3 text-sm text-[#5E636E] whitespace-pre-line">{r.description}</p>
                    {(r.doc_types_required || []).length > 0 && (
                        <div className="mt-3">
                            <div className="co-label">What we need</div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {r.doc_types_required.map((d) => (
                                    <span key={d} className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2.5 py-1 text-xs">
                                        {labelFor(DOC_TYPES, d)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {r.jotform?.signing_url && r.jotform?.status === "awaiting_signature" && (
                        <a
                            href={r.jotform.signing_url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-600 transition-colors w-full sm:w-auto"
                            data-testid={`portal-req-sign-${r.id}`}
                        >
                            Sign now <ArrowRight className="h-4 w-4" />
                        </a>
                    )}
                    {r.jotform?.status === "completed" && (
                        <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 text-xs" data-testid={`portal-req-signed-${r.id}`}>
                            ✓ Signed and submitted via Jotform
                        </div>
                    )}
                    {r.status !== "completed" && r.status !== "cancelled" && (
                        <Link
                            to={`/portal/upload?requestId=${r.id}${r.doc_types_required?.[0] ? `&type=${r.doc_types_required[0]}` : ""}`}
                            className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-[#171717] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#2E2E2E] transition-colors w-full sm:w-auto"
                            data-testid={`portal-req-upload-${r.id}`}
                        >
                            Upload documents <ArrowRight className="h-4 w-4" />
                        </Link>
                    )}
                </li>
            ))}
        </ul>
    );
}
